import type { CaptureDiagnosis } from '../vocal/diagnose-capture.js'
import { closeBandCents } from '../vocal/constants.js'
import { wholeCents, type NoteScore, type PhraseScore } from '../vocal/score-phrase.js'

/**
 * The map from a `VocalLib` verdict to the lexicon entries that describe it.
 *
 * This module exists so the UI never composes a feedback sentence. Pitch Trainer is the first
 * tool in the suite whose output is a *measurement of the user*, and a surface that assembled
 * its own prose could tell a singer something no one wrote and no one reviewed. Every string
 * they read is one locked §12 entry, chosen here, from numbers computed there.
 *
 * Pure and total: `null` in — the state where nothing passed the voicing gate — is a first-class
 * outcome, not an error and not a zero.
 *
 * Spec: docs/superpowers/specs/2026-09-06-vocal-pitch-training-design.md §5.4, §5.5, §5.6, §5.7, §12
 */

/**
 * How far a sung interval may sit from the reference interval and still count as "the intervals
 * were right" for §5.5's headline.
 *
 * Tight, because the headline's whole value is that it is a *different diagnosis* from "you sing
 * out of tune": intervals intact plus a whole-phrase offset means a support or anchor problem
 * and points at a completely different practice remedy. If the intervals are only roughly right
 * that inference does not hold, and printing it would send the singer to the wrong exercise.
 * 15 ¢ sits between the 10 ¢ just-noticeable difference for melodic pitch and the 20 ¢ "on the
 * note" band, so an interval that reads as intact here would also read as on the note there.
 * @see spec §5.5
 */
export const relativeIntactCents = 15

/**
 * ...and how far the phrase as a whole must have settled before there is anything to say about
 * it. Below this the offset is inside the tuning noise of a good take, and the headline would be
 * telling a singer to fix something they did not do.
 * @see spec §5.5
 */
export const driftHeadlineCents = 15

/**
 * ...and how close to a whole-semitone shift the phrase must sit to have started on a different
 * note (Bowie, GH #2328 item 1, amended on #2342). It is the "close" band: within it the singer
 * was close to a semitone (or two) away. A uniform 51–59 ¢ sits between two notes, and "a semitone
 * low" would claim a starting note the singer never reached; that is drift, "about half a semitone".
 */
export const transposedToleranceCents = closeBandCents

export interface NoteFeedback {
  /** One of the six §5.4 band entries. */
  bandLexiconId: string
  /** Unsigned whole cents, for the entry's `{{cents}}`. The direction is in the id. */
  cents: number
  /** `vocal-steady` or `vocal-wavering`. Reported, never scored (§5.6). */
  stabilityLexiconId: string
}

export interface PhraseFeedback {
  /** `scorePhrase` returned null: nothing passed the voicing gate. No score is shown. */
  nothingHeard: boolean
  perNote: NoteFeedback[]
  /** Reference notes the singer did not sing. Rendered beside `vocal-missed-note`. */
  missedCount: number
  /** Notes the singer sang that are not in the reference. Rendered beside `vocal-extra-note`. */
  extraCount: number
  /** `vocal-octave-under` / `-over`, or null when the phrase was at pitch. */
  octaveLexiconId: string | null
  /** The §5.5 headline entry, or null when the finding does not hold. */
  headlineLexiconId: string | null
  /**
   * The headline entry's tokens: `cents` for drift; `semitones` and `distance` ("a semitone",
   * "2 semitones") for a transposed phrase. Empty when there is no headline.
   */
  headlineTokens: Record<string, string | number>
  /** Mean of the per-note scores, 0-100. Null when nothing was heard. */
  scorePercent: number | null
}

const BAND_IDS: Record<Exclude<NoteScore['band'], 'wrong-note'>, { under: string; over: string }> = {
  on: { under: 'vocal-on-note', over: 'vocal-on-note' },
  close: { under: 'vocal-close-under', over: 'vocal-close-over' },
  off: { under: 'vocal-off-under', over: 'vocal-off-over' },
}

const bandLexiconId = (entry: NoteScore): string => {
  if (entry.band === 'wrong-note') {
    return 'vocal-wrong-note'
  }
  const pair = BAND_IDS[entry.band]
  return entry.cents < 0 ? pair.under : pair.over
}

/**
 * §5.5's headline, and the one place this module makes a judgement the spec left implicit.
 *
 * The spec's condition is "every `perInterval.cents` within ±15 and `driftCents` defined with
 * |drift| > 15". `every` over an empty list is vacuously true, so a one-note item sung 30 cents
 * flat would satisfy it and the singer would be told their intervals were right. A one-note item
 * has no intervals. It is required to have at least one — flagged for Bowie rather than assumed.
 */
const headlineFor = (score: PhraseScore): { id: string; tokens: Record<string, string | number> } | null => {
  const { perInterval } = score.relative
  if (perInterval.length === 0) {
    return null
  }
  if (!perInterval.every(interval => Math.abs(interval.cents) <= relativeIntactCents)) {
    return null
  }
  /**
   * The offset over ALL paired notes, wrong notes included, for both headlines. Intact intervals
   * already say the notes moved together, so a note past the semitone line is the phrase's offset,
   * not a melodic error. `relative.driftCents` drops wrong notes, so on a phrase straddling the
   * 50 ¢ line (some notes −45, some −55) it would understate the offset (Bowie, #2342).
   */
  const { perNote } = score.absolute
  const offset = perNote.reduce((total, entry) => total + entry.cents, 0) / perNote.length
  const semitones = Math.round(Math.abs(offset) / 100)
  if (semitones >= 1 && Math.abs(Math.abs(offset) - semitones * 100) <= transposedToleranceCents) {
    return {
      id: offset < 0 ? 'vocal-good-relative-transposed-under' : 'vocal-good-relative-transposed-over',
      tokens: { semitones, distance: semitones === 1 ? 'a semitone' : `${semitones} semitones` },
    }
  }
  if (Math.abs(offset) <= driftHeadlineCents) {
    return null
  }
  return {
    id: offset < 0 ? 'vocal-good-relative-drift-under' : 'vocal-good-relative-drift-over',
    tokens: { cents: wholeCents(offset) },
  }
}

const OCTAVE_IDS: Record<-1 | 0 | 1, string | null> = {
  '-1': 'vocal-octave-under',
  '0': null,
  '1': 'vocal-octave-over',
}

export const toPhraseFeedback = (score: PhraseScore | null): PhraseFeedback => {
  if (score === null) {
    return {
      nothingHeard: true,
      perNote: [],
      missedCount: 0,
      extraCount: 0,
      octaveLexiconId: null,
      headlineLexiconId: null,
      headlineTokens: {},
      scorePercent: null,
    }
  }

  const headline = headlineFor(score)

  return {
    nothingHeard: false,
    perNote: score.absolute.perNote.map((entry, index) => ({
      bandLexiconId: bandLexiconId(entry),
      cents: wholeCents(entry.cents),
      stabilityLexiconId: score.stability[index] === 'wavering' ? 'vocal-wavering' : 'vocal-steady',
    })),
    missedCount: score.missed.length,
    extraCount: score.extra.length,
    octaveLexiconId: OCTAVE_IDS[score.octave],
    headlineLexiconId: headline?.id ?? null,
    headlineTokens: headline?.tokens ?? {},
    scorePercent: Math.round(score.absolute.mean * 100),
  }
}

const DIAGNOSIS_IDS: Record<CaptureDiagnosis['kind'], string> = {
  'no-signal': 'vocal-no-signal',
  'too-quiet': 'vocal-too-quiet',
  unclear: 'vocal-unclear',
  /**
   * The capture itself was fine — frames were loud enough and clear enough — and still no
   * note survived segmentation. That is a note too short to outlive the 100 ms scoop, and there
   * is no state for it yet. The generic sentence is the honest answer: a diagnosis the numbers
   * do not support would be worse than none.
   */
  ok: 'vocal-nothing-heard',
}

/**
 * Which sentence to show when `scorePhrase` returned null.
 *
 * "We didn't hear anything" is true of all four cases and useful in none of them: a user whose
 * input device is wrong will sing louder for ever if the tool asks them to. The diagnosis names
 * the one thing they can change.
 */
export const captureDiagnosisLexiconId = (diagnosis: CaptureDiagnosis | null): string =>
  diagnosis === null ? 'vocal-nothing-heard' : DIAGNOSIS_IDS[diagnosis.kind]
